<template>
  <v-calendar
    ref="calendar"
    :value="day"
    class="schedule"
    color="primary"
    type="week-by-entity"
    event-start="date_from"
    event-end="date_to"
    event-entity-id="employee_id"
    :start="startDate"
    :end="endDate"
    :holidays="holidays"
    :events="parsedTasks"
    :event-timed="isTimedEvent"
    :weekdays="weekdays"
    :entities="FILTERED_EMPLOYEES"
    :event-color="getEventColor"
    :short-months="false"
    :short-weekdays="false"
    :selected-days="selectedDays"
    :requested-vacations="employeesRequestedVacations"
    :padding-day="2"
    @input="setDay"
    @change="getEvents"
    @click:day="clickDay"
    @event-drop="handleEventDrop"
  >
    <template #entity-label="{first_name, last_name, info}">
      {{ `${first_name} ${last_name}` }}
      <v-tooltip v-if="info" right max-width="400">
        <template v-slot:activator="{ on, attrs }">
          <v-avatar class="ml-1" left size="22" color="#e0e0e0">
            <v-icon small color="black" v-bind="attrs" v-on="on">
              mdi-information-variant
            </v-icon>
          </v-avatar>
        </template>
        <span>{{ info }}</span>
      </v-tooltip>
    </template>
    <template #event="{event}">
      <v-row :class="conflictClass(event)" dense>
        <v-col class="pa-0">
          <v-tooltip v-if="event.conflict" top>
            <template v-slot:activator="{ on }">
              <v-icon class="pl-1" small color="white" v-on="on"
                >mdi-information-variant-circle-outline</v-icon
              >
            </template>
            <span>{{ event.conflict.region }}: {{ event.conflict.title }}</span>
          </v-tooltip>
          <strong class="pl-1">{{ eventTime(event) }}</strong>
          {{ eventName(event) }}
        </v-col>
      </v-row>
    </template>
  </v-calendar>
</template>

<script>
import moment from 'moment';
import { mapActions, mapState, mapGetters } from 'vuex';
import query from '@/modules/_global/mixins/query';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import VCalendar from '@/modules/_global/_components/calendar/VCalendar';
import { LOCKED_TIME } from '../common/reasons';
import { mapCacheActions } from 'vuex-cache';

export const DATE_FORMAT = moment.HTML5_FMT.DATE;

export default {
  name: 'Schedule',

  components: {
    VCalendar
  },

  mixins: [query, i18nRefreshData],

  data: () => ({
    weekdays: [1, 2, 3, 4, 5, 6, 0]
  }),

  computed: {
    ...mapState('holidays', ['holidays']),
    ...mapState('planning', [
      'day',
      'tasks',
      'filters',
      'selectedDay',
      'selectedEntity',
      'employeesRequestedVacations',
      'isMultiTaskUpdate'
    ]),
    ...mapGetters('planning', ['FILTERED_EMPLOYEES']),
    startDate() {
      return moment(this.day)
        .startOf('isoWeek')
        .format(DATE_FORMAT);
    },
    endDate() {
      return moment(this.startDate)
        .endOf('isoWeek')
        .format(DATE_FORMAT);
    },
    selectedDays() {
      if (!this.selectedDay || !this.selectedEntity) {
        return [];
      }

      return [
        {
          entity_id: this.selectedEntity.id,
          date: this.selectedDay.date
        }
      ];
    },
    parsedTasks() {
      return this.tasks.map(task => {
        if (this.isTimedEvent(task)) {
          const dateTimeFrom = `${task.date_from}T${task.time_from}:00`;
          const dateTimeTo = task.date_to
            ? `${task.date_to}T${task.time_to}:00`
            : `${task.date_from}T${task.time_to}:00`;

          return {
            ...task,
            date_from: new Date(dateTimeFrom),
            date_to: new Date(dateTimeTo)
          };
        }

        return {
          ...task,
          date_from: new Date(task.date_from),
          date_to: new Date(task.date_to || task.date_from)
        };
      });
    }
  },

  watch: {
    filters: {
      deep: true,
      handler: 'getEvents'
    },
    isMultiTaskUpdate: {
      deep: true,
      handler: 'getEvents'
    },
    day: {
      deep: true,
      handler: 'dayWatchHandler'
    }
  },
  mounted() {
    this.watchStore();
  },

  destroyed() {
    this.watchStore();
  },

  methods: {
    ...mapCacheActions('holidays', ['getHolidays']),

    ...mapActions('planning', [
      'getTasks',
      'setDay',
      'setDayEntity',
      'getEmployeesRequestedVacations'
    ]),

    handleEventDrop({ event, day, entity }) {
      const data = { day, entity };
      this.setDayEntity(data);
      this.$emit('event-dropped', { event, day, entity });
    },

    watchStore() {
      this.$store.watch(
        () => this.$store.getters['planning/GET_DAY'],
        () => this.dayWatchHandler()
      );
    },

    refreshData() {
      this.getEvents();
    },

    dayWatchHandler() {
      if (!this.day) {
        return;
      }
      this.getWeekHolidays();
    },

    getRequestedVacations(params) {
      this.getEmployeesRequestedVacations({
        params: params
      }).catch(error => {
        this.error = error;
      });
    },

    getWeekHolidays() {
      this.getHolidays({
        params: {
          end_date: this.endDate,
          start_date: this.startDate
        }
      }).catch(error => {
        this.error = error;
      });
    },

    prev() {
      this.$refs.calendar.prev();
    },

    next() {
      this.$refs.calendar.next();
    },

    getEventColor(event) {
      return event.color;
    },

    isTimedEvent({ reason }) {
      return !(reason && reason.id !== LOCKED_TIME);
    },

    eventTime(event) {
      const { time_from, time_to } = event;

      return this.isTimedEvent(event) ? `${time_from}-${time_to}` : '';
    },

    eventName(event) {
      const { reason, order } = event;

      return reason ? reason.name : order.city;
    },

    getEvents() {
      const { employees } = this.filters;

      if (!employees.length || !this.day) {
        return;
      }

      const params = {
        employees,
        date_from: this.day
      };

      const vacationParams = {
        date_from: this.startDate,
        date_to: this.endDate,
        employees: employees
      };

      this.getTasks({ params });
      this.getRequestedVacations(vacationParams);
    },

    clickDay(event) {
      this.setDayEntity(event);
    },

    conflictClass(event) {
      return {
        'ma-0': true,
        'v-event-conflict': !!event.conflict
      };
    }
  }
};
</script>

<style lang="scss">
.schedule {
  .v-event-all-day {
    height: 100% !important;
    width: 100% !important;
  }

  .v-event:has(.v-event-conflict) {
    box-shadow: inset 0 0 0 1px white, 0 0 0 1px black,
      inset 0 0 4px 4px rgba(0, 0, 0, 0.4);
  }

  .v-event {
    cursor: grab;
  }
  .v-event:active {
    cursor: grabbing;
  }
  .v-calendar-weekly-entities__day {
    cursor: pointer;
  }
}
</style>
